// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartTileFrontSide.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.  </copyright>
// ----------------------------------------------------------------------
import React, { Component, PropTypes } from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import reportPartTypeManager from 'Izenda.ReportPartTypeManager';
import TileTitleFrontSide from '../Izenda.TileTitleFrontSide';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import DashboardFilterDescriptions from './Izenda.DashboardFilterDescriptions';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import VerticalLayout from 'share/components/controls/Izenda.VerticalLayout';
import Dimensions from 'share/components/hoc/Izenda.Dimensions';
import commonDataStore from 'logic/Izenda.ReportDesignerCommonDataStore';
import { isEmpty } from 'lodash/fp';
import DashboardPartRequiredFilter from './Izenda.DashboardPartRequiredFilter';
import { observable } from 'mobx';

const ConditionalFilterDescription = observer(function ConditionalFilterDescription({ store, dashboardPart }) {
  if (!store.dashboardDetail.dashboard.showFilterDescription) {
    return null;
  }
  return <DashboardFilterDescriptions dashboardPart={dashboardPart} />;
});

@observer
class ReportPartTileFrontSide extends Component {
  static displayName = 'ReportPartTileFrontSide';

  static propTypes = {
    dashboardPart: PropTypes.object.isRequired,
    store: PropTypes.object.isRequired
  };

  @observable dashboardInfoHeight: ?number = null;

  constructor() {
    super();

    this.renderListeners = [];
    this.tileTitleFrontSideKey = 'TileTitleFrontSide';
    this.frontSideKey = 'FrontSide';
    PrintingUtils.pushRenderListener(this.renderListeners, this.tileTitleFrontSideKey);
    PrintingUtils.pushRenderListener(this.renderListeners, this.frontSideKey);
    commonDataStore.loadCustomDataFormat();
  }

  publishFinishedRender = () => {
    const props = this.props;

    props.printingMode &&
      PrintingUtils.publishFinishedRender(this.renderListeners, () => {
        props.finishedRender && props.finishedRender();
      });
  };

  componentDidMount() {
    this.publishFinishedRender();
  }

  componentDidUpdate() {
    if (this.props.printingMode) {
      const node = this.sectionRef || {};
      this.dashboardInfoHeight = node && node.getBoundingClientRect().height;
    }

    this.publishFinishedRender();
  }

  shouldComponentUpdate(nextProps: any, nextState: any) {
    return !(this.props.printingMode && this.props.fulfilledData === nextProps.fulfilledData);
  }
  setSectionRef = (dom: any) => {
    dom && (this.sectionRef = dom);
  };

  render() {
    const {
      dashboardPart,
      dashboardPart: { reportPart },
      printingMode,
      cellWidth,
      fulfilledData,
      store
    } = this.props;
    const renderListeners = this.renderListeners;

    if (ReportPartUtils.needToRenderLazyLoadingBar(reportPart, printingMode)) {
      return ReportPartUtils.renderLazyLoadingBar();
    }

    const FrontSide = reportPartTypeManager.getReportFrontSide(
      reportPart.type,
      reportPartTypeManager.DISPLAY_SECTION.Dashboard
    );
    const haveScrollBox = reportPartTypeManager.getReportPartType(reportPart.type).haveDashboardScroll;
    const viewClassName = printingMode ? 'u-posRelative' : '';
    const haveEmptyRequiredFilter = !isEmpty(dashboardPart.emptyVisibleRequiredFilter());
    const dashboardElement = haveEmptyRequiredFilter ? (
      <DashboardPartRequiredFilter filters={dashboardPart.emptyVisibleRequiredFilter()} />
    ) : (
      <section ref={this.setSectionRef}>
        <TileTitleFrontSide
          dashboardPart={dashboardPart}
          printingMode={printingMode}
          finishedRender={PrintingUtils.getFinishedRenderPromiseById(renderListeners, this.tileTitleFrontSideKey)}
          fulfilledData={fulfilledData}
        />
        <ConditionalFilterDescription {...this.props} />
      </section>
    );

    // get new real height of report part (minus height of dashboardElement).
    const reportPartElementStyle = printingMode
      ? dashboardPart.getReportPartSize(cellWidth, {
          width: 0,
          height: this.dashboardInfoHeight || 0
        })
      : null;
    const lookupErrorMessage = reportPart.getLookupErrorMessage();
    const reportPartElement = isEmpty(dashboardPart.emptyVisibleRequiredFilter()) ? (
      !!lookupErrorMessage ? (
        lookupErrorMessage
      ) : (
        <FrontSide
          reportPart={reportPart}
          store={store}
          style={reportPartElementStyle}
          dashboardPart={dashboardPart}
          printingMode={printingMode}
          forDashboard
          finishedRender={PrintingUtils.getFinishedRenderPromiseById(renderListeners, this.frontSideKey)}
          fulfilledData={fulfilledData}
        />
      )
    ) : null;

    if (haveScrollBox || printingMode || haveEmptyRequiredFilter) {
      const className = classnames({
        u_fullSize: !printingMode
      });

      return (
        <div className={className}>
          <ScrollBox fullSize={printingMode} viewClassName={viewClassName}>
            <div className='u_fullSize'>
              {dashboardElement}
              {reportPartElement}
            </div>
          </ScrollBox>
        </div>
      );
    }

    return <ReportPartTileFrontSideWrapper dashboardElement={dashboardElement} reportPartElement={reportPartElement} />;
  }
}

const ReportPartTileFrontSideWrapper = Dimensions(
  observer(({ dashboardElement, reportPartElement }) => {
    return (
      <div className='u_fullSize'>
        <div className='u_fullHeight u_overflowHidden'>
          <VerticalLayout>
            {dashboardElement}
            {reportPartElement}
          </VerticalLayout>
        </div>
      </div>
    );
  })
);

export default ReportPartTileFrontSide;



// WEBPACK FOOTER //
// ./ui/modules/Dashboard/DashboardDesigner/DashboardGrid/DashboardTile/ReportPartTile/Izenda.ReportPartTileFrontSide.js